
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { PERMISSION } from '@/permissions';
import AnnouncementsModal from '@/components/announcements/AnnouncementsModal.vue';
import CustomQuestionsModal from '@/components/advanced/CustomQuestionsModal.vue';
import InvoiceSettings from '@/components/advanced/InvoiceSettings.vue';
import OrderOptionsComponent from '@/components/advanced/OrderOptions.vue';
import InquiryFormModal from '@/components/advanced/InquiryFormModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'auth';

@Component<OperationSettings>({
	components: {
		AnnouncementsModal,
		CustomQuestionsModal,
		InvoiceSettings,
		'order-options': OrderOptionsComponent,
		InquiryFormModal,
		TextButton,
		ValidationObserver
	}
})
export default class OperationSettings extends Vue {
	@Getter('isCateringRestaurant', { namespace }) private isCateringRestaurant!: boolean;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('isOrderManagementEnabled', { namespace }) private isOrderManagementEnabled!: boolean;
	@Getter('isMarketplaceEnabled', { namespace }) private isMarketplaceEnabled!: boolean;
	@Getter('areCateringEventsEnabled', { namespace }) private areCateringEventsEnabled!: boolean;
	@Getter('getCheckoutQuestions', { namespace: 'customQuestions' }) private checkoutQuestions!: CustomQuestion[];
	@Getter('getBeoQuestions', { namespace: 'customQuestions' }) private beoQuestions!: CustomQuestion[];
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;
	@Watch('restaurant')
	private onRestaurantChange(): void {
		this.setOperationsData();
	}

	$refs!: { observer: Validate };

	private PERMISSION = PERMISSION;
	private showCustomQuestionsModal: boolean = false;
	private showBeoFieldsModal: boolean = false;
	private showInquiryFormModal: boolean = false;
	private showAnnouncementsModal: boolean = false;
	private orderOptions: OrderOptions|null = null;
	private menuManagerConfig: MenuManagerConfig = this.restaurant?.menu_manager_config || {} as MenuManagerConfig;


	private created(): void {
		this.setOperationsData();
	}

	private setOperationsData(): void {
		this.setOrderOptions();
		this.setMenuManagerConfig();
	}

	/**
	 * Set order options
	 *
	 * @return {void}
	 */
	private setOrderOptions(): void {
		this.orderOptions = this.restaurant.order_options || {} as OrderOptions;
	}

	/**
	 * Set configuration of menu manager
	 *
	 * @return {void}
	 */
	private setMenuManagerConfig(): void {
		this.menuManagerConfig = this.restaurant.menu_manager_config || {};
	}

	/**
	 * Update order options from event
	 *
	 * @param {OrderOptions} orderOptions
	 * @return {void}
	 */
	private updateOrderOptions(orderOptionsObject: OrderOptions): void {
		this.orderOptions = orderOptionsObject;
	}

	/**
	 * Validate operations settings and emit save event to parent component
	 * Is called by Settings.vue when the save button is clicked
	 *
	 * @return {Promise<void>}
	 */
	public async save(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (isValid) {
			const updatedRestaurant = {
				...this.restaurant,
				order_options: this.orderOptions,
				menu_manager_config: this.menuManagerConfig
			};
			this.$emit('save-operations', updatedRestaurant);
		}
	}
}
